import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useSyncExternalStore,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import avatarImage from "../assets/images/avatar.svg";
import birthdayIcon from "../assets/images/birthdayicon.svg";
import incidentIcon from "../assets/images/incident.svg";
import medicalTreatmentIcon from "../assets/images/medical-treatment-icon.svg";
import {
  calculateAge,
  formatCalendarDate,
  formatDate,
  getClientId,
  getCaseId,
  getToken,
  setClientId,
  setCaseId,
} from "../Utils/helper";
import { api_without_cancellation } from "../api/api";
import {
  fetchAllPages,
  fetchAllStages,
  fetchCommutativeChecklist,
  fetchCurrentCase,
  setCurrentCase,
  setCaseSummary,
  setCommutativeChecklist,
} from "../Redux/caseData/caseDataSlice";
import { fetchCurrentClient, setCurrentClient } from "../Redux/client/clientSlice";
import { setClientProviders } from "../Redux/client-providers/clientProviderSlice";
import { setHeaderSummary } from "../Redux/header/headerDataSlice";
import { fetchCaseSummary, fetchHeaderSummary } from "../api/case";
import { fetchCasePageClientProviders } from "../api/clientProviders";
import { isAlwaysAllowedRoute, normalizeRouteKey } from "../Routes/routeAccessPolicy.mjs";
import { getStoredFirmLoginPath, isPublicAuthPath } from "../auth/firmLogin.mjs";

import { shouldValidateAccess, validationIdsMatch } from "./accessValidationPolicy.mjs";
import { getOpeningPageIcon, getOpeningPageName, matchLoadingCase } from "./caseLoadingDetails.mjs";
import { caseLoadTracker } from "./caseLoadTracker.mjs";

import "./caseLoading.css";

const AccessValidationContext = createContext();
const LOADING_MILESTONES = [
  { key: "access", loadingLabel: "Checking access", loadedLabel: "Access confirmed" },
  { key: "client", loadingLabel: "Loading client details", loadedLabel: "Client details loaded" },
  { key: "case", loadingLabel: "Loading case details", loadedLabel: "Case details loaded" },
];

// Three separate dots fade in one by one, so the animation works in a proportional font.
const LoadingDots = () => (
  <span className="case-loading-dots" aria-hidden="true">
    <span>.</span>
    <span>.</span>
    <span>.</span>
  </span>
);

export const AccessDeniedScreen = () => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
      <p className="text-gray-600 mb-4">You don&apos;t have permission to access this</p>
    </div>
  </div>
);

const getCaseLoadingDetails = (caseData, caseId) => {
  const client = caseData?.for_client || caseData?.client_data;
  const clientName =
    client?.entity_name || [client?.last_name, client?.first_name].filter(Boolean).join(", ");

  return {
    client,
    name: clientName || (caseId ? `Case #${caseId}` : "Case"),
    hasClientName: Boolean(clientName),
    // profile_pic_63p is the largest size the API sends.
    avatar: client?.profile_pic_63p || client?.profile_pic_29p || avatarImage,
    type: caseData?.case_type?.name,
    typeIcon: caseData?.case_type?.casetype_icon,
    incidentDate: caseData?.incident_date,
  };
};

export const LoadingOverlay = ({
  caseData,
  caseId,
  clientId = caseData?.for_client?.id || caseData?.client_data?.id,
  destinationPath,
  overPage = false,
}) => {
  const location = useLocation();
  const pages = useSelector((state) => state.allPages?.allPages);
  const casePages = useSelector((state) => state.caseData?.pages);
  const pageCatalog = [
    ...(Array.isArray(casePages) ? casePages : []),
    ...(Array.isArray(pages) ? pages : []),
  ];
  const pathname = destinationPath || location.pathname;
  const pageName = getOpeningPageName(pathname, pageCatalog);
  const pageIcon = ["treatment", "bp-medicalmin"].includes(normalizeRouteKey(pathname))
    ? medicalTreatmentIcon
    : getOpeningPageIcon(pathname, pageCatalog);
  const caseLoad = useSyncExternalStore(caseLoadTracker.subscribe, caseLoadTracker.getSnapshot);
  const milestones = validationIdsMatch(caseLoad, { clientId, caseId })
    ? caseLoad.milestones
    : null;
  // Keep the log in the agreed order even when case and client requests finish in parallel.
  const visibleMilestones = [];
  for (const milestone of LOADING_MILESTONES) {
    const isLoaded = Boolean(milestones?.[milestone.key]);
    visibleMilestones.push({ ...milestone, isLoaded });
    if (!isLoaded) break;
  }
  const detailsLoaded = visibleMilestones.every(({ isLoaded }) => isLoaded);
  const { client, name, avatar, type, typeIcon, incidentDate } = getCaseLoadingDetails(
    caseData,
    caseId
  );
  const isCompany = client?.person_company === "Company";

  return (
    <div
      className={`case-loading-screen${overPage ? " case-loading-screen--over-page" : ""}`}
      role="status"
      aria-live="polite"
    >
      {!overPage && (
        <div className="case-loading-shell" aria-hidden="true">
          <div className="case-loading-shell-header" />
          <div className="case-loading-shell-sidebar" />
          <div className="case-loading-shell-content" />
        </div>
      )}
      <div className="case-loading-backdrop" />
      <div className="case-loading-popup">
        <div className="case-loading-popup-header">Opening Case</div>
        {/* Same layout as the client tab at the top of the case header (NewNavbarProfile). */}
        <div className="case-loading-client-row">
          <span className="case-loading-avatar" aria-hidden="true">
            <img src={avatar} alt="" />
          </span>
          <div className="case-loading-client-copy">
            <h1 className="case-loading-title clientTabFont">
              {client?.last_name || client?.first_name ? (
                <>
                  {client.last_name}
                  {client.last_name && client.first_name && (
                    <span className="text-dark-grey">,&nbsp;</span>
                  )}
                  {client.first_name}
                </>
              ) : (
                name
              )}
            </h1>
            <div className="case-loading-client-info">
              {isCompany && client?.main_contact?.name ? (
                <>
                  <i className="ic ic-client case-loading-detail-icon" aria-hidden="true" />
                  <span>{client.main_contact.name}</span>
                </>
              ) : (
                !isCompany &&
                client?.birthday && (
                  <>
                    <img className="case-loading-detail-icon" src={birthdayIcon} alt="" />
                    <span>{formatCalendarDate(client.birthday)}</span>
                    <span className="text-primary-25">age</span>
                    <span>{calculateAge(client.birthday)}</span>
                  </>
                )
              )}
            </div>
          </div>
        </div>
        <div className="case-loading-details">
          {type && (
            <div className="case-loading-detail-row">
              {typeIcon ? (
                <img className="case-loading-detail-icon" src={typeIcon} alt="" />
              ) : (
                <i className="ic ic-case-navigator ic-19" aria-hidden="true" />
              )}
              <span>{type}</span>
            </div>
          )}
          {incidentDate && (
            <div className="case-loading-detail-row">
              <img className="case-loading-detail-icon" src={incidentIcon} alt="" />
              <span>{formatDate(incidentDate)}</span>
            </div>
          )}
        </div>
        <div className="case-loading-detail-row case-loading-page-row">
          <span className="case-loading-label">Loading page</span>
          {pageIcon ? (
            <img className="case-loading-detail-icon" src={pageIcon} alt="" />
          ) : (
            <i className="ic ic-case-navigator ic-19" aria-hidden="true" />
          )}
          {/*  */}
          <span>{pageName}</span>
        </div>
        <div className="case-loading-progress">
          {visibleMilestones.map(({ key, loadingLabel, loadedLabel, isLoaded }) => (
            <div
              className={`case-loading-progress-line${isLoaded ? "" : " case-loading-progress-line--active"}`}
              key={key}
            >
              <span className="case-loading-progress-marker" aria-hidden="true">
                {isLoaded ? "✓" : "›"}
              </span>
              <span className="case-loading-progress-text">
                {isLoaded ? loadedLabel : loadingLabel}
              </span>
              {!isLoaded && <LoadingDots />}
            </div>
          ))}
          {detailsLoaded && (
            <div className="case-loading-progress-line case-loading-progress-line--active">
              <span className="case-loading-progress-marker" aria-hidden="true">
                ›
              </span>
              {pageIcon ? (
                <img className="ic ic-15" src={pageIcon} alt="" />
              ) : (
                <i className="ic ic-case-navigator ic-19" aria-hidden="true" />
              )}
              <span className="case-loading-progress-text">Loading {pageName} page</span>
              <LoadingDots />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const validationCache = new Map();
const CACHE_DURATION = 30 * 60 * 1000;
export const AccessValidationProvider = ({ children }) => {
  const [validationState, setValidationState] = useState({
    isValid: null,
    isLoading: false,
    error: null,
    validatedIds: { clientId: null, caseId: null },
    loadingIds: null,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const recentCases = useSelector((state) => state.caseData?.lastAccessedCases);
  const currentCase = useSelector((state) => state.caseData?.current);
  const headerSummary = useSelector((state) => state.headerData?.headerSummary);
  const currentValidationRef = useRef(null);
  const manualValidationRef = useRef(false);
  const pendingManualNavigationRef = useRef(null);
  const loadingCaseSnapshotRef = useRef({ key: null, caseData: null });
  const hasInitialized = useRef(false);
  const previousIds = useRef({ clientId: null, caseId: null });
  const openingNavigationRef = useRef(null);
  const caseLoad = useSyncExternalStore(caseLoadTracker.subscribe, caseLoadTracker.getSnapshot);

  const shouldSkipValidation = useMemo(() => {
    return isPublicAuthPath(location.pathname);
  }, [location.pathname]);

  const currentIds = useMemo(() => {
    const pathParts = location.pathname.split("/");
    let urlClientId = null;
    let urlCaseId = null;

    for (let i = 0; i < pathParts.length - 1; i++) {
      if (pathParts[i] && pathParts[i + 1] && pathParts[i + 2]) {
        const potential1 = pathParts[i + 1];
        const potential2 = pathParts[i + 2];

        if (potential1.match(/^\d+$/) || potential1.match(/^[0-9a-f-]{36}$/i)) {
          urlClientId = potential1;
          if (potential2.match(/^\d+$/) || potential2.match(/^[0-9a-f-]{36}$/i)) {
            urlCaseId = potential2;
            break;
          }
        }
      }
    }

    const clientId = urlClientId || getClientId() || localStorage.getItem("clientId");
    const caseId = urlCaseId || getCaseId() || localStorage.getItem("caseId");

    return { clientId, caseId, fromUrl: Boolean(urlClientId && urlCaseId) };
  }, [location.pathname, location.key]);

  // Refresh explicit case openings even when reopening the same client/case.
  // Global routes such as Search keep their existing no-popup behavior.
  const isOpeningCaseNavigation =
    currentIds.fromUrl &&
    location.state?.refreshCaseData === true &&
    Boolean(matchLoadingCase(location.state?.openingCase, currentIds));
  const openingNavigationPending =
    isOpeningCaseNavigation && openingNavigationRef.current !== location.key;
  const waitingForOpeningNavigation =
    isOpeningCaseNavigation && validationState.openingNavigationKey !== location.key;
  const shouldSkipCaseValidation = isAlwaysAllowedRoute(location.pathname) && !currentIds.fromUrl;

  // Case-scoped validation starts only when the full client/case pair exists.
  const shouldSkipDueToMissingIds = useMemo(() => {
    return !currentIds.clientId || !currentIds.caseId;
  }, [currentIds.clientId, currentIds.caseId]);

  // Updated skip validation logic
  const shouldCompletelySkipValidation = useMemo(() => {
    return shouldSkipValidation || shouldSkipCaseValidation || shouldSkipDueToMissingIds;
  }, [shouldSkipCaseValidation, shouldSkipValidation, shouldSkipDueToMissingIds]);

  const validationMatchesCurrentIds = validationIdsMatch(validationState.validatedIds, currentIds);
  const accessValidationRequired = shouldValidateAccess({
    hasInitialized: hasInitialized.current,
    validatedIds: validationState.validatedIds,
    currentIds,
  });

  const validateAccess = useCallback(
    async (
      clientId,
      caseId,
      { skipAccessCheck = false, refreshRelatedData = false, openingNavigationKey = null } = {}
    ) => {
      if (!clientId || !caseId || clientId === "undefined" || caseId === "undefined") {
        setValidationState({
          isLoading: false,
          isValid: true,
          error: null,
          validatedIds: { clientId: null, caseId: null },
        });
        return true;
      }
      const now = Date.now();
      const cacheKey = `${clientId}-${caseId}`;
      const validationId = Symbol(cacheKey);
      currentValidationRef.current = validationId;
      previousIds.current = { clientId, caseId };
      // Queue the loading state before the synchronous tracker notification, so
      // reopening the same case cannot briefly mount the destination twice.
      setValidationState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        loadingIds: { clientId, caseId },
      }));
      const loadGeneration = caseLoadTracker.begin(clientId, caseId);

      const loadCaseDetails = async () => {
        setClientId(clientId);
        setCaseId(caseId);
        dispatch(setCurrentCase(null));
        dispatch(setCurrentClient(null));
        dispatch(setCaseSummary(null));
        dispatch(setHeaderSummary(null));
        dispatch(setClientProviders(null));
        dispatch(setCommutativeChecklist(null));
        const [caseResponse, clientResponse, , pagesResponse] = await Promise.all([
          dispatch(fetchCurrentCase(clientId, caseId)).then((data) => {
            if (data?.case) caseLoadTracker.completeMilestone("case", loadGeneration);
            return data;
          }),
          dispatch(fetchCurrentClient(clientId)).then((data) => {
            if (data) caseLoadTracker.completeMilestone("client", loadGeneration);
            return data;
          }),
          fetchHeaderSummary(clientId, caseId).then((data) => {
            if (currentValidationRef.current === validationId) {
              dispatch(setHeaderSummary(data));
            }
          }),
          // Recheck the destination case's pages after access is approved. Persisted
          // sidebar data can otherwise deny an allowed page before it refreshes.
          dispatch(fetchAllPages(caseId, true)),
          ...(refreshRelatedData
            ? [
                fetchCaseSummary(clientId, caseId).then((data) => {
                  if (currentValidationRef.current === validationId) dispatch(setCaseSummary(data));
                }),
                fetchCasePageClientProviders(clientId, caseId).then((data) => {
                  if (currentValidationRef.current === validationId)
                    dispatch(setClientProviders(data?.results));
                }),
                dispatch(fetchCommutativeChecklist(clientId, caseId)),
                dispatch(fetchAllStages()),
              ]
            : []),
        ]);
        if (
          !caseResponse?.case ||
          !clientResponse ||
          (refreshRelatedData && pagesResponse === undefined)
        ) {
          throw new Error("The case details could not be loaded. Please try again.");
        }
      };

      const cached = validationCache.get(cacheKey);
      const freshCached = cached && now - cached.timestamp < CACHE_DURATION ? cached : null;

      try {
        const response =
          skipAccessCheck || freshCached
            ? {
                data: {
                  access: skipAccessCheck || freshCached.isValid,
                  message: freshCached?.error,
                },
              }
            : await api_without_cancellation.post("/api/check-attorney-access/", {
                client_id: clientId,
                case_id: caseId,
              });
        if (currentValidationRef.current !== validationId) {
          return;
        }

        const isValid = response.data.access;
        const error = isValid ? null : response.data.message || "Access denied";

        if (isValid) {
          caseLoadTracker.completeMilestone("access", loadGeneration);
          await loadCaseDetails();
          if (currentValidationRef.current !== validationId) return;
        }

        if (!skipAccessCheck) {
          validationCache.set(cacheKey, { isValid, error, timestamp: now });
        }
        if (!isValid) caseLoadTracker.reset();

        setValidationState({
          isValid,
          isLoading: false,
          error,
          validatedIds: { clientId, caseId },
          loadingIds: { clientId, caseId },
          openingNavigationKey,
        });
        return isValid;
      } catch (error) {
        if (currentValidationRef.current !== validationId) {
          return;
        }

        console.error("Validation error:", error);
        const errorMessage =
          error.response?.data?.message || error.message || "The case could not be loaded.";
        caseLoadTracker.reset();

        setValidationState({
          isValid: false,
          isLoading: false,
          error: errorMessage,
          loadFailed: true,
          validatedIds: { clientId, caseId },
          loadingIds: { clientId, caseId },
          openingNavigationKey,
        });
        return false;
      } finally {
        if (currentValidationRef.current === validationId) {
          manualValidationRef.current = false;
          hasInitialized.current = true;
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const pendingManualNavigation = pendingManualNavigationRef.current;
    if (pendingManualNavigation) {
      if (validationIdsMatch(pendingManualNavigation, currentIds)) {
        pendingManualNavigationRef.current = null;
      } else if (location.pathname === pendingManualNavigation.sourcePath) {
        return;
      } else {
        pendingManualNavigationRef.current = null;
        manualValidationRef.current = false;
        currentValidationRef.current = null;
      }
    }

    if (manualValidationRef.current) return;

    if (shouldSkipValidation) {
      currentValidationRef.current = null;
      caseLoadTracker.reset();
      setValidationState({
        isValid: true,
        isLoading: false,
        error: null,
        validatedIds: { clientId: null, caseId: null },
      });
      hasInitialized.current = true;
      return;
    }

    const token = getToken();
    if (!token) {
      navigate(getStoredFirmLoginPath());
      return;
    }

    if (shouldSkipCaseValidation || shouldSkipDueToMissingIds) {
      currentValidationRef.current = null;
      caseLoadTracker.reset();
      setValidationState({
        isValid: true,
        isLoading: false,
        error: null,
        validatedIds: { clientId: null, caseId: null },
      });
      hasInitialized.current = true;
      return;
    }

    if (validationState.isLoading && validationIdsMatch(previousIds.current, currentIds)) return;
    // The tracker publishes synchronously before React commits isLoading. Do
    // not start the same explicit opening again during that intermediate render.
    const activeOpening = caseLoadTracker.getSnapshot();
    if (
      isOpeningCaseNavigation &&
      !openingNavigationPending &&
      activeOpening.isOpening &&
      validationIdsMatch(activeOpening, currentIds)
    )
      return;

    if (accessValidationRequired || openingNavigationPending) {
      if (isOpeningCaseNavigation) openingNavigationRef.current = location.key;
      validateAccess(currentIds.clientId, currentIds.caseId, {
        skipAccessCheck: isAlwaysAllowedRoute(location.pathname) && !isOpeningCaseNavigation,
        refreshRelatedData: isOpeningCaseNavigation,
        openingNavigationKey: isOpeningCaseNavigation ? location.key : null,
      });
    }
  }, [
    shouldCompletelySkipValidation, // Updated dependency
    shouldSkipCaseValidation,
    shouldSkipDueToMissingIds,
    shouldSkipValidation,
    location.pathname,
    location.key,
    isOpeningCaseNavigation,
    openingNavigationPending,
    navigate,
    accessValidationRequired,
    currentIds.clientId,
    currentIds.caseId,
    validateAccess,
    validationState.isLoading,
  ]);

  useEffect(() => {
    let timeoutId;

    const handleStorageChange = (e) => {
      if (e.key === "clientId" || e.key === "caseId") {
        // Updated to use the new skip condition
        if (shouldCompletelySkipValidation || manualValidationRef.current) {
          return;
        }

        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const freshClientId = getClientId() || localStorage.getItem("clientId");
          const freshCaseId = getCaseId() || localStorage.getItem("caseId");

          if (
            freshClientId !== previousIds.current.clientId ||
            freshCaseId !== previousIds.current.caseId
          ) {
            validateAccess(freshClientId, freshCaseId);
          }
        }, 200);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearTimeout(timeoutId);
    };
  }, [shouldCompletelySkipValidation, validateAccess]); // Updated dependency

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      for (const [key, value] of validationCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
          validationCache.delete(key);
        }
      }
    }, CACHE_DURATION);

    return () => clearInterval(interval);
  }, []);

  const retry = useCallback(async () => {
    const retryIds = pendingManualNavigationRef.current || currentIds;
    const cacheKey = `${retryIds.clientId}-${retryIds.caseId}`;
    validationCache.delete(cacheKey);
    hasInitialized.current = false; // Reset initialization to allow retry
    const opened = await validateAccess(retryIds.clientId, retryIds.caseId, {
      skipAccessCheck:
        !pendingManualNavigationRef.current &&
        isAlwaysAllowedRoute(location.pathname) &&
        !isOpeningCaseNavigation,
      refreshRelatedData: isOpeningCaseNavigation,
      openingNavigationKey: isOpeningCaseNavigation ? location.key : null,
    });
    if (opened && pendingManualNavigationRef.current) {
      navigate(`/bp-case/${retryIds.clientId}/${retryIds.caseId}`);
    }
  }, [
    currentIds,
    location.pathname,
    location.key,
    isOpeningCaseNavigation,
    navigate,
    validateAccess,
  ]);

  // useEffect(() => {
  //   const clearPersistor = async () => {
  //     console.log("hi");
  //     persistor.pause();
  //     await persistor.flush();
  //     await persistor.purge();
  //   };

  //   clearPersistor();
  // }, [currentIds.clientId, currentIds.caseId]);

  const validateAccessManually = useCallback(
    async (clientId, caseId) => {
      manualValidationRef.current = true;
      pendingManualNavigationRef.current = {
        clientId,
        caseId,
        sourcePath: location.pathname,
      };

      // Clear cache for these IDs
      const cacheKey = `${clientId}-${caseId}`;
      validationCache.delete(cacheKey);

      setValidationState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        loadingIds: { clientId, caseId },
      }));

      return validateAccess(clientId, caseId);
    },
    [location.pathname, validateAccess]
  );

  const waitingForManualNavigation =
    pendingManualNavigationRef.current &&
    // Treatment resolves its case from storage rather than URL parameters.
    !validationIdsMatch(pendingManualNavigationRef.current, currentIds);
  const manualOpeningFailed =
    pendingManualNavigationRef.current &&
    !validationState.isLoading &&
    validationState.isValid === false &&
    validationIdsMatch(validationState.validatedIds, pendingManualNavigationRef.current);
  const pageCanMount =
    !waitingForOpeningNavigation &&
    !waitingForManualNavigation &&
    !validationState.isLoading &&
    (shouldCompletelySkipValidation || validationMatchesCurrentIds) &&
    validationState.isValid === true;
  const waitingForPageRequests =
    !shouldCompletelySkipValidation &&
    validationIdsMatch(caseLoad, currentIds) &&
    caseLoad.isOpening;
  const shouldShowLoading =
    !manualOpeningFailed &&
    (Boolean(waitingForManualNavigation) ||
      waitingForOpeningNavigation ||
      validationState.isLoading ||
      (!shouldCompletelySkipValidation && !validationMatchesCurrentIds) ||
      (pageCanMount && waitingForPageRequests));
  // Counts case openings (each time the "Opening Case" popup appears). Intro animations key off
  // it, so they replay on every open, including going back to a case seen earlier. The popup only
  // shows when the client/case changes, so switching tabs within a case doesn't replay them.
  const [caseOpenings, setCaseOpenings] = useState(0);
  useEffect(() => {
    if (shouldShowLoading) setCaseOpenings((count) => count + 1);
  }, [shouldShowLoading]);

  const loadingIds =
    validationState.isLoading || pendingManualNavigationRef.current
      ? validationState.loadingIds || currentIds
      : currentIds;
  // The clicked card travels with the navigation so the first frame already
  // has the destination client, including cases shared by multiple clients.
  const loadingCaseCandidate =
    matchLoadingCase(location.state?.openingCase, loadingIds) ||
    recentCases?.map((item) => matchLoadingCase(item, loadingIds)).find(Boolean) ||
    matchLoadingCase(currentCase, loadingIds) ||
    matchLoadingCase(
      { ...headerSummary?.case_data, for_client: headerSummary?.client_data },
      loadingIds
    );
  const loadingCaseKey = `${loadingIds.clientId}-${loadingIds.caseId}`;
  if (loadingCaseSnapshotRef.current.key !== loadingCaseKey) {
    loadingCaseSnapshotRef.current = { key: loadingCaseKey, caseData: null };
  }
  if (loadingCaseCandidate) {
    const previousCase = loadingCaseSnapshotRef.current.caseData;
    const candidateHasClientName = getCaseLoadingDetails(
      loadingCaseCandidate,
      loadingIds.caseId
    ).hasClientName;
    loadingCaseSnapshotRef.current.caseData = {
      ...previousCase,
      ...loadingCaseCandidate,
      for_client: candidateHasClientName
        ? loadingCaseCandidate.for_client
        : previousCase?.for_client || loadingCaseCandidate.for_client,
      case_type: loadingCaseCandidate.case_type?.name
        ? loadingCaseCandidate.case_type
        : previousCase?.case_type || loadingCaseCandidate.case_type,
      incident_date: loadingCaseCandidate.incident_date || previousCase?.incident_date,
    };
  }
  const loadingCase = loadingCaseSnapshotRef.current.caseData;

  const contextValue = useMemo(
    () => ({
      ...validationState,
      retry,
      validateAccessManually,
      currentIds,
      loadingCase,
      loadingIds,
      // True while the "Opening Case" popup covers the (already mounted, hidden) page.
      loadingScreenVisible: shouldShowLoading,
      caseOpenings,
    }),
    [
      validationState,
      retry,
      validateAccessManually,
      currentIds,
      loadingCase,
      loadingIds,
      shouldShowLoading,
      caseOpenings,
    ]
  );
  return (
    <AccessValidationContext.Provider value={contextValue}>
      {pageCanMount && (
        <div
          key={`${currentIds.clientId}-${currentIds.caseId}`}
          style={{ display: "contents", visibility: shouldShowLoading ? "hidden" : undefined }}
          aria-hidden={shouldShowLoading || undefined}
          inert={shouldShowLoading ? "" : undefined}
        >
          {children}
        </div>
      )}
      {shouldShowLoading && (
        <LoadingOverlay
          caseData={loadingCase}
          clientId={loadingIds.clientId}
          caseId={loadingIds.caseId}
        />
      )}
      {!shouldShowLoading &&
        validationState.isValid === false &&
        (validationState.loadFailed ? (
          <div className="case-loading-screen">
            <div className="case-loading-popup p-3" role="alert">
              <p>{validationState.error}</p>
              <button type="button" className="btn btn-primary mt-2" onClick={retry}>
                Retry Opening Case
              </button>
            </div>
          </div>
        ) : (
          <AccessDeniedScreen />
        ))}
    </AccessValidationContext.Provider>
  );
};

export const useAccessValidation = () => {
  const context = useContext(AccessValidationContext);
  if (!context) {
    throw new Error("useAccessValidation must be used within AccessValidationProvider");
  }
  return context;
};

/**
 * For intro animations: whether the "Opening Case" popup is showing, and how many times a case has
 * been opened (part of the animation key, so each open replays it). Safe outside the provider.
 */
export const useCaseOpening = () => {
  const context = useContext(AccessValidationContext);
  return {
    loadingScreenVisible: Boolean(context?.loadingScreenVisible),
    caseOpenings: context?.caseOpenings ?? 0,
  };
};

export const CasePageReady = () => {
  const { currentIds } = useAccessValidation();
  const { generation } = useSyncExternalStore(
    caseLoadTracker.subscribe,
    caseLoadTracker.getSnapshot
  );
  useEffect(() => {
    if (validationIdsMatch(caseLoadTracker.getSnapshot(), currentIds)) {
      caseLoadTracker.pageMounted(generation);
    }
  }, [currentIds, generation]);
  return null;
};

export const withAccessValidation = (Component) => {
  return (props) => {
    const validation = useAccessValidation();
    return <Component {...props} validation={validation} />;
  };
};

export default AccessValidationProvider;
